import { useState, useEffect } from 'react';
import type { Room, User } from '../types';
import { useRoomsStore, useAuthStore, usePresenceStore, useMessagesStore } from '../store';
import { api } from '../api/client';
import { NewChatModal } from './NewChatModal';

function formatTime(unix: number): string {
  const d = new Date(unix * 1000);
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  if (diff < 60000) return 'Baru saja';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} mnt`;
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('id', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('id', { day: '2-digit', month: 'short' });
}

function getLastMessagePreview(room: Room): string {
  const m = room.last_message;
  if (!m) return 'Belum ada pesan';
  if (m.type === 'image') return '📸 Foto';
  if (m.type === 'location') return '📍 Lokasi';
  return m.content.length > 40 ? m.content.slice(0, 40) + '…' : m.content;
}

function Avatar({ user, size = 40 }: { user?: User | null; name?: string; size?: number }) {
  const name = user?.name || '?';
  const initials = name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  const colors = ['from-blue-500 to-cyan-500', 'from-purple-500 to-pink-500', 'from-green-500 to-emerald-500', 'from-orange-500 to-red-500'];
  const colorIdx = name.charCodeAt(0) % colors.length;

  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={name}
        style={{ width: size, height: size }}
        className="avatar"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size }}
      className={`rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-white font-semibold flex-shrink-0`}
    >
      <span style={{ fontSize: size * 0.38 }}>{initials}</span>
    </div>
  );
}

interface SidebarProps {
  onSelectRoom: (room: Room) => void;
}

export function Sidebar({ onSelectRoom }: SidebarProps) {
  const { user, clearAuth } = useAuthStore();
  const { rooms, setRooms, activeRoomId, setActiveRoom, clearUnread, resetRooms } = useRoomsStore();
  const { resetMessages } = useMessagesStore();
  const { resetPresence, onlineUsers } = usePresenceStore();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    api.rooms.list().then(({ rooms }) => {
      setRooms(rooms);
      setLoading(false);
    }).catch(console.error);
  }, []);

  const handleSelectRoom = (room: Room) => {
    setActiveRoom(room.id);
    clearUnread(room.id);
    onSelectRoom(room);
  };

  const handleLogout = async () => {
    const fcmToken = localStorage.getItem('fcm_token');
    if (fcmToken) {
      await api.users.unregisterFcmToken(fcmToken).catch(err => {
        console.error('[FCM] Error unregistering token on logout:', err);
      });
      localStorage.removeItem('fcm_token');
    }
    await api.auth.logout().catch(() => {});
    resetRooms();
    resetMessages();
    resetPresence();
    clearAuth();
  };

  const filteredRooms = rooms.filter(r => {
    const name = r.type === 'personal' ? r.partner?.name || r.name : r.name;
    return name?.toLowerCase().includes(search.toLowerCase());
  });

  const getRoomName = (room: Room) =>
    room.type === 'personal' ? (room.partner?.name || 'Chat Pribadi') : (room.name || 'Grup');

  const getRoomAvatar = (room: Room) =>
    room.type === 'personal' ? room.partner : undefined;

  return (
    <div className="flex flex-col h-full bg-dark-800 border-r border-dark-700">
      {/* Header */}
      <div className="p-4 border-b border-dark-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">GC</span>
            </div>
            <span className="font-bold text-white text-lg">GarutChat</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowNewChat(true)}
              className="p-2 rounded-xl hover:bg-white/[0.06] text-dark-300 hover:text-white transition-all"
              title="Chat baru"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <div className="relative">
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="p-1 rounded-xl hover:bg-white/[0.06] transition-all"
              >
                <Avatar user={user} size={32} />
              </button>
              {showProfile && (
                <div className="absolute right-0 top-full mt-2 w-48 glass rounded-2xl p-2 shadow-2xl z-50 animate-fade-in">
                  <div className="px-3 py-2 border-b border-dark-600 mb-1">
                    <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-dark-400 text-xs truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-red-400 hover:bg-red-500/10 text-sm transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Keluar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Cari percakapan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-dark-700 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-dark-400 outline-none focus:ring-2 focus:ring-brand-500/30 transition-all"
          />
        </div>
      </div>

      {/* Room list */}
      <div className="flex-1 overflow-y-auto py-2 px-2">
        {loading ? (
          <div className="space-y-2 p-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl">
                <div className="w-11 h-11 rounded-full shimmer flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 shimmer rounded-full w-3/4" />
                  <div className="h-3 shimmer rounded-full w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="text-4xl mb-3">💬</div>
            <p className="text-dark-400 text-sm">
              {search ? 'Tidak ada chat yang cocok' : 'Belum ada percakapan. Mulai chat baru!'}
            </p>
          </div>
        ) : (
          filteredRooms.map((room) => {
            const name = getRoomName(room);
            const avatarUser = getRoomAvatar(room);
            const isActive = room.id === activeRoomId;
            const isOnline = room.type === 'personal' && room.partner && onlineUsers.has(room.partner.id);
            const unread = room.unread_count || 0;

            return (
              <div
                key={room.id}
                onClick={() => handleSelectRoom(room)}
                className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
              >
                {/* Avatar */}
                <div className="relative">
                  {room.type === 'group' ? (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {(room.name || 'G')[0].toUpperCase()}
                    </div>
                  ) : (
                    <Avatar user={avatarUser} size={44} />
                  )}
                  {isOnline && (
                    <div className="absolute -bottom-0.5 -right-0.5 online-dot" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-white text-sm truncate">{name}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {room.last_message && (
                        <span className="text-xs text-dark-500">
                          {formatTime(room.last_message.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="text-xs text-dark-400 truncate">{getLastMessagePreview(room)}</span>
                    {unread > 0 && (
                      <span className="flex-shrink-0 w-5 h-5 bg-brand-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showNewChat && <NewChatModal onClose={() => setShowNewChat(false)} onRoomCreated={handleSelectRoom} />}
    </div>
  );
}
